import { AriaOrb } from "../AriaOrb";
import { Volume2, VolumeX, Download, FileText, Radio, Ear } from "lucide-react";

const WAKE_TITLE = {
  listening: "Wake-word active — say 'Hey ARIA'",
  denied: "Microphone permission denied — enable to use wake-word",
  error: "Wake-word listener error — will retry",
};
const WAKE_CLASS = {
  listening: "border-[var(--aria-accent)]/50 text-[var(--aria-accent)] bg-[var(--aria-accent)]/8",
  denied: "border-red-500/40 text-red-400 bg-red-500/8",
};
const WAKE_LABEL = { listening: "Hey ARIA", denied: "Mic blocked" };

function statusLabel(streaming, recording) {
  if (streaming) return "Thinking…";
  if (recording) return "Listening…";
  return "Ready";
}
function statusDot(streaming, recording) {
  if (streaming) return "bg-[var(--aria-accent)] animate-pulse";
  if (recording) return "bg-red-500 animate-pulse";
  return "bg-[var(--aria-primary)]";
}

export function ChatHeader({ nickname, accent, orbState, streaming, recording, activeId, flags, docs, docFilter,
  onDocFilter, voiceMode, onToggleVoice, onExport, onOpenConversation, wakeStatus }) {
  const showWake = flags.voice_always_on && flags.voice_wake_word && wakeStatus !== "unsupported";
  return (
    <header className="px-3 sm:px-6 py-3 border-b border-[var(--aria-border)] aria-glass flex flex-wrap items-center justify-between gap-3 sticky top-0 z-10">
      <div className="flex items-center gap-3">
        <AriaOrb size={36} state={orbState} color={accent} />
        <div>
          <div className="font-display text-lg leading-none tracking-tight">{nickname}</div>
          <div className="text-[11px] uppercase tracking-[0.15em] text-[var(--aria-text-muted)] mt-1.5 font-mono flex items-center gap-1.5">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${statusDot(streaming, recording)}`} />
            {statusLabel(streaming, recording)}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap min-w-0 items-center gap-2">
        {activeId && flags.chat_export && (
          <>
            <button onClick={() => onExport("pdf")} data-testid="export-pdf-button" title="Export this conversation as PDF"
              className="btn px-3 py-1.5 text-sm rounded-md border border-[var(--aria-border)] hover:border-[var(--aria-text)] flex items-center gap-1.5 transition-colors">
              <FileText size={14} /> PDF
            </button>
            <button onClick={() => onExport("csv")} data-testid="export-csv-button" title="Export this conversation as CSV"
              className="btn px-3 py-1.5 text-sm rounded-md border border-[var(--aria-border)] hover:border-[var(--aria-text)] flex items-center gap-1.5 transition-colors">
              <Download size={14} /> CSV
            </button>
          </>
        )}
        <select value={docFilter} onChange={e => onDocFilter(e.target.value)} data-testid="document-filter"
          className="max-w-[170px] min-w-0 px-3 py-1.5 text-sm rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none hover:border-[var(--aria-text)] transition-colors">
          <option value="all">All documents</option>
          {docs.map(d => <option key={d.document_id} value={d.document_id}>{d.name}</option>)}
        </select>
        <button onClick={onToggleVoice} data-testid="voice-mode-toggle" title="Auto-speak ARIA's replies"
          className={`btn px-3 py-1.5 text-sm rounded-md border flex items-center gap-2 transition-colors ${voiceMode
            ? "bg-[var(--aria-primary)] text-white border-[var(--aria-primary)]"
            : "border-[var(--aria-border)] hover:border-[var(--aria-primary)] hover:text-[var(--aria-primary)]"}`}>
          {voiceMode ? <Volume2 size={14} /> : <VolumeX size={14} />} TTS {voiceMode ? "ON" : "OFF"}
        </button>
        {flags.voice_always_on && (
          <button onClick={onOpenConversation} data-testid="conversation-mode-launch"
            title={flags.voice_wake_word ? "Hands-free conversation — say 'Hey ARIA' or click" : "Hands-free conversation mode"}
            className="btn px-3.5 py-1.5 text-sm rounded-md text-white font-medium flex items-center gap-2 transition-all ring-glow-accent hover:scale-[1.02]"
            style={{ background: "var(--aria-grad-brand)" }}>
            <Radio size={14} className="animate-pulse" /> Conversation
          </button>
        )}
        {showWake && (
          <div data-testid="wake-word-status" data-state={wakeStatus} title={WAKE_TITLE[wakeStatus] || "Wake-word idle"}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.18em] rounded-sm border transition-colors ${WAKE_CLASS[wakeStatus] || "border-[var(--aria-border)] text-[var(--aria-text-muted)]"}`}>
            <Ear size={11} className={wakeStatus === "listening" ? "animate-pulse" : ""} />
            {WAKE_LABEL[wakeStatus] || "Wake"}
          </div>
        )}
      </div>
    </header>
  );
}
